import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Form, Row, Col, Button, Table, Badge } from 'reactstrap';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';
import { StyledCard } from '../Base/Common/CommonUIComponents';
import { CustomTable } from '../Base/Common/CustomTable';
import { getAPI } from '../Base/API';
import { getExtrasAccessOptions } from '../Base/Common/ReferenceDataFunctions';
import { channelInstanceFields, formatValue, normalize } from './ChannelInstanceChanges';

// Fields compared by the API, in the order of the configuration screens; credentials only show that they changed
const historyFields = [
    ...channelInstanceFields.filter(field => field.type !== 'credential'),
    { key: 'active', labelId: 'ChannelList.Active', type: 'bool' },
    { key: 'mode', labelId: 'generic.Mode', type: 'text' },
    { key: 'clientUrl', labelId: 'ChannelInstanceAdminConfig.ClientUrl', groupId: 'ChannelConfig.AdminConfig', type: 'text' },
    { key: 'excludeReservationSources', labelId: 'ChannelInstanceAdminConfig.ExcludeReservationSources', groupId: 'ChannelConfig.AdminConfig', type: 'text' },
    { key: 'extrasAccess', labelId: 'ChannelInstanceAdminConfig.ExtrasAccess', groupId: 'ChannelConfig.AdminConfig', type: 'extras' },
    { key: 'migrationChannel', labelId: 'ChannelInstanceAdminConfig.migrationChannel', groupId: 'ChannelConfig.AdminConfig', type: 'bool' },
    { key: 'autoFillConsent', labelId: 'ChannelInstanceAdminConfig.autoFillConsent', groupId: 'ChannelConfig.AdminConfig', type: 'bool' },
    { key: 'channelNameAsSource', labelId: 'ChannelInstanceAdminConfig.channelNameAsSource', groupId: 'ChannelConfig.AdminConfig', type: 'bool' },
    { key: 'useProxy', labelId: 'ChannelInstanceAdminConfig.useProxy', groupId: 'ChannelConfig.AdminConfig', type: 'bool' }
];

// the API names changed fields as in C# (MaxInventoryDays); values and previousValues come camel cased
const changedKeys = version => (version.changedFields || []).map(field => field.charAt(0).toLowerCase() + field.slice(1));

// Full table: every field sits in a collapsible group. The sections of the configuration screen keep their
// titles; the fields above them (no section title there) and the channel status get groups of their own
const statusKeys = ['active', 'channelConfigState', 'mode'];
const credentialsRow = { key: 'credentials', labelId: 'ChannelInstanceHistory.Credentials', type: 'credentials' };
const sectionIds = [...new Set(historyFields.filter(field => field.groupId).map(field => field.groupId))];

const historyGroups = [
    { id: 'ChannelInstanceHistory.GeneralData', fields: [...historyFields.filter(field => !field.groupId && !statusKeys.includes(field.key)), credentialsRow] },
    { id: 'ChannelInstanceHistory.ChannelStatus', fields: statusKeys.map(key => historyFields.find(field => field.key === key)) },
    ...sectionIds.map(id => ({ id, fields: historyFields.filter(field => field.groupId === id) }))
];

const isChanged = (version, field) => field.type === 'credentials' ? version.credentialsChanged : changedKeys(version).includes(field.key);

// the full table shows versions as columns, so fewer of them fit on screen
const pageSizes = { changes: 10, full: 5 };

class ChannelInstanceHistory extends Component {

    constructor(props) {
        super(props);
        this.searchButton = React.createRef();
        this.state = {
            block: true,
            error: [],
            mode: 'changes',
            historyList: [],
            pageIndex: 0,
            pageSize: pageSizes.changes,
            totalItems: 0,
            startDate: null,
            endDate: null,
            focusedInput: null,
            collapsedGroups: [],
            hoveredVersion: null
        };
    }

    componentDidMount() {
        this.getHistory();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.channelInstanceId !== this.props.channelInstanceId) {
            this.setState({ block: true, pageIndex: 0, historyList: [], totalItems: 0 }, this.getHistory);
        }
    }

    getHistory = () => {
        const { pageIndex, pageSize, startDate, endDate } = this.state;
        const { channelInstanceId } = this.props;

        let url = `/api/Rate/ChannelConfig/v1/ChannelInstance/${encodeURIComponent(channelInstanceId)}/History?pageIndex=${pageIndex}&pageSize=${pageSize}`;

        if (startDate) {
            url += `&startDate=${moment(startDate).format('YYYY-MM-DD')}`;
        }
        if (endDate) {
            url += `&endDate=${moment(endDate).format('YYYY-MM-DD')}`;
        }

        getAPI(result => {
            // the channel changed while this request was in flight: its answer belongs to the previous one
            if (channelInstanceId !== this.props.channelInstanceId) {
                return;
            }

            const { data, error } = result;

            if (error) {
                this.setState({ block: false, error: [{ message: error.message, stack: error.stack, messageType: 'danger' }] });
                return;
            }
            if (data && data.errors && data.errors.length > 0) {
                this.setState({ block: false, error: data.errors.map(err => ({ message: err.message, messageType: 'danger' })) });
                return;
            }

            this.setState({ block: false, error: [], historyList: (data && data.responseList) || [], totalItems: (data && data.count) || 0 });
        }, url);
    }

    goToPage = (pageIndex, pageSize = this.state.pageSize) => {
        this.setState({ block: true, pageIndex, pageSize }, this.getHistory);
    }

    search = e => {
        e.preventDefault();
        this.goToPage(0);
    }

    // each mode has its own page size, so switching goes back to the first page
    changeMode = mode => {
        if (mode !== this.state.mode) {
            this.setState({ mode, block: true, pageIndex: 0, pageSize: pageSizes[mode] }, this.getHistory);
        }
    }

    handleTableChange = (_, { page, sizePerPage }) => {
        this.goToPage(page - 1, sizePerPage);
    }

    // UpdatedAt is stored in UTC
    formatDate = date => moment.utc(date).local().format('YYYY-MM-DD HH:mm');

    formatField = (value, field) => field.type === 'extras' ?
        this.formatExtrasAccess(value)
        : formatValue(normalize(Array.isArray(value) ? value.join(', ') : value, field.type), field.type, this.props.intl);

    // null means every extras API is allowed and an empty list blocks them all, so the two can't both read "empty"
    formatExtrasAccess = value => {
        const { intl } = this.props;

        if (value === null || value === undefined) {
            return intl.formatMessage({ id: 'ChannelInstanceHistory.AllExtras' });
        }
        if (value.length === 0) {
            return intl.formatMessage({ id: 'ChannelInstanceHistory.NoExtras' });
        }

        const options = getExtrasAccessOptions();

        return value.map((code, index) => {
            const option = options.find(opt => opt.value === code);

            return <React.Fragment key={code}>{index > 0 ? ', ' : ''}{option ? option.label : code}</React.Fragment>;
        });
    }

    fieldLabel = field => {
        const { intl } = this.props;
        const label = intl.formatMessage({ id: field.labelId });

        return field.groupId ? `${intl.formatMessage({ id: field.groupId })}: ${label}` : label;
    }

    userName = version => version.updatedByName || version.updatedBySurname ?
        `${version.updatedByName || ''} ${version.updatedBySurname || ''}`.trim()
        : this.props.intl.formatMessage({ id: 'ChannelInstanceHistory.System' });

    renderChanges = version => {
        if (!version.hasPrevious) {
            return <span className="text-muted"><FormattedMessage id="ChannelInstanceHistory.FirstVersion" /></span>;
        }

        if (version.isMinor) {
            return (
                <span>
                    <i className="fas fa-sync-alt mr-1" />
                    <FormattedMessage id={version.tokensChanged ? 'ChannelInstanceHistory.TokenRefresh' : 'ChannelInstanceHistory.NoChanges'} />
                </span>
            );
        }

        const changed = changedKeys(version);

        return (
            <>
                {historyFields.filter(field => changed.includes(field.key)).map(field => (
                    <div key={field.key}>
                        {this.fieldLabel(field)}: <span className="text-muted">{this.formatField(version.previousValues && version.previousValues[field.key], field)}</span> → <span className="table-warning px-1">{this.formatField(version[field.key], field)}</span>
                    </div>
                ))}
                {version.credentialsChanged ?
                    <div className="text-muted">
                        <i className="fas fa-lock mr-1" />
                        <FormattedMessage id="ChannelInstanceHistory.CredentialsChanged" />
                    </div>
                    : ''}
            </>
        );
    }

    toggleGroup = groupId => {
        this.setState(({ collapsedGroups }) => ({
            collapsedGroups: collapsedGroups.includes(groupId) ? collapsedGroups.filter(id => id !== groupId) : [...collapsedGroups, groupId]
        }));
    }

    hoverVersion = version => {
        if (version !== this.state.hoveredVersion) {
            this.setState({ hoveredVersion: version });
        }
    }

    // the version column under the mouse is highlighted; a changed cell keeps its own colour
    cellClass = (version, changed, muted = false) => [
        changed ? 'table-warning' : version.version === this.state.hoveredVersion ? 'table-active' : '',
        muted ? 'text-muted' : ''
    ].join(' ').trim();

    renderFieldRow = field => (
        <tr key={field.key}>
            <td className="text-muted pl-4" onMouseEnter={() => this.hoverVersion(null)}><FormattedMessage id={field.labelId} /></td>
            {this.state.historyList.map(version => (
                <td key={version.version} className={this.cellClass(version, isChanged(version, field), version.isMinor)} onMouseEnter={() => this.hoverVersion(version.version)}>
                    {field.type === 'credentials' ?
                        version.credentialsChanged ? <FormattedMessage id="ChannelInstanceHistory.ChangedShort" /> : '••••'
                        : this.formatField(version[field.key], field)}
                </td>
            ))}
        </tr>
    );

    // a collapsed group still marks the versions where one of its fields changed
    renderGroupRows = group => {
        const collapsed = this.state.collapsedGroups.includes(group.id);

        return [
            <tr key={group.id} role="button" onClick={() => this.toggleGroup(group.id)}>
                <td className="font-weight-bold" onMouseEnter={() => this.hoverVersion(null)}>
                    <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'} mr-2`} />
                    <FormattedMessage id={group.id} />
                </td>
                {this.state.historyList.map(version => (
                    <td key={version.version} className={this.cellClass(version, collapsed && group.fields.some(field => isChanged(version, field)))} onMouseEnter={() => this.hoverVersion(version.version)} />
                ))}
            </tr>,
            ...(collapsed ? [] : group.fields.map(this.renderFieldRow))
        ];
    }

    renderFullTable = () => {
        const { historyList, pageIndex, pageSize, totalItems } = this.state;

        if (historyList.length === 0) {
            return <p className="text-muted small"><FormattedMessage id="generic.table.nodata" /></p>;
        }

        return (
            <>
                <Table size="sm" responsive hover className="small" onMouseLeave={() => this.hoverVersion(null)}>
                    <thead>
                        <tr>
                            <th onMouseEnter={() => this.hoverVersion(null)}><FormattedMessage id="ChannelInstanceChanges.Field" /></th>
                            {historyList.map(version => (
                                <th key={version.version} className={this.cellClass(version, false, version.isMinor)} onMouseEnter={() => this.hoverVersion(version.version)}>
                                    v{version.version}
                                    {version.isCurrent ? <Badge color="primary" className="ml-1"><FormattedMessage id="ChannelInstanceHistory.Current" /></Badge> : ''}
                                    <span className="d-block font-weight-normal">{this.formatDate(version.updatedAt)}</span>
                                    <span className="d-block font-weight-normal text-muted">{this.userName(version)}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {historyGroups.map(this.renderGroupRows)}
                    </tbody>
                </Table>
                <div className="d-flex justify-content-between align-items-center small">
                    <FormattedMessage id="generic.table.totalRows" values={{ from: pageIndex * pageSize + 1, to: pageIndex * pageSize + historyList.length, size: totalItems }} />
                    <div>
                        <Button size="sm" className="btn-host mr-1" disabled={pageIndex === 0} onClick={() => this.goToPage(pageIndex - 1)}>
                            <i className="fas fa-chevron-left" />
                        </Button>
                        <Button size="sm" className="btn-host" disabled={(pageIndex + 1) * pageSize >= totalItems} onClick={() => this.goToPage(pageIndex + 1)}>
                            <i className="fas fa-chevron-right" />
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    render() {
        const { intl } = this.props;
        const { block, error, mode, historyList, pageIndex, pageSize, totalItems } = this.state;

        const columns = [
            {
                dataField: 'updatedAt',
                text: intl.formatMessage({ id: 'ChannelInstanceHistory.Date' }),
                formatter: (cell, row) => (
                    <>
                        {this.formatDate(cell)}
                        {row.isCurrent ? <Badge color="primary" className="ml-1"><FormattedMessage id="ChannelInstanceHistory.Current" /></Badge> : ''}
                    </>
                )
            },
            {
                dataField: 'updatedBy',
                text: intl.formatMessage({ id: 'ChannelInstanceHistory.User' }),
                formatter: (_, row) => (
                    <>
                        {this.userName(row)}
                        {row.updatedByEmail ? <span className="d-block text-muted">{row.updatedByEmail}</span> : ''}
                    </>
                )
            },
            {
                dataField: 'version',
                text: intl.formatMessage({ id: 'ChannelInstanceHistory.Version' })
            },
            {
                dataField: 'changedFields',
                text: intl.formatMessage({ id: 'ChannelInstanceHistory.Changes' }),
                formatter: (_, row) => this.renderChanges(row)
            }
        ];

        return (
            <StyledCard block={block} error={error} title="ChannelConfig.History">
                <Form onSubmit={this.search}>
                    <Row className="mb-3 align-items-center">
                        <Col className="col-12 col-md mb-2 mb-md-0">
                            <DateRangePicker
                                startDate={this.state.startDate ? moment(this.state.startDate).locale(moment.locale()) : null}
                                startDateId="channelInstanceHistoryStartDate"
                                isOutsideRange={day => day >= moment().add(1, 'days')}
                                endDate={this.state.endDate ? moment(this.state.endDate).locale(moment.locale()) : null}
                                endDateId="channelInstanceHistoryEndDate"
                                onDatesChange={({ startDate, endDate }) => this.setState({ startDate, endDate })}
                                focusedInput={this.state.focusedInput}
                                onFocusChange={focusedInput => this.setState({ focusedInput })}
                                small={true}
                                numberOfMonths={1}
                                showDefaultInputIcon={true}
                                showClearDates={true}
                                // picking the dates with the mouse leaves the form; focusing the submit button lets Enter search
                                onClose={() => this.searchButton.current && this.searchButton.current.focus()}
                                renderMonthElement={({ month }) => moment(month).locale(intl.locale).format('MMMM YYYY')}
                            />
                        </Col>
                        <Col className="col-auto">
                            <div className="btn-group btn-group-sm">
                                <button type="button" className={`btn ${mode === 'changes' ? 'btn-host' : 'btn-outline-secondary'}`} onClick={() => this.changeMode('changes')}>
                                    <i className="fas fa-list mr-1" />
                                    <FormattedMessage id="ChannelInstanceHistory.Changes" />
                                </button>
                                <button type="button" className={`btn ${mode === 'full' ? 'btn-host' : 'btn-outline-secondary'}`} onClick={() => this.changeMode('full')}>
                                    <i className="fas fa-table mr-1" />
                                    <FormattedMessage id="ChannelInstanceHistory.FullTable" />
                                </button>
                            </div>
                        </Col>
                        <Col className="col-auto pl-0">
                            <Button type="submit" innerRef={this.searchButton} className="btn btn-sm btn-host">
                                <i className="fas fa-search" />
                            </Button>
                        </Col>
                    </Row>
                </Form>
                <Row>
                    <Col>
                        {mode === 'changes' ?
                            <CustomTable
                                data={historyList}
                                columns={columns}
                                keyField="version"
                                showTotal={true}
                                page={pageIndex + 1}
                                sizePerPage={pageSize}
                                totalSize={totalItems}
                                remote={true}
                                onTableChange={this.handleTableChange}
                                rowClasses={row => row.isMinor ? 'small text-muted' : ''}
                            />
                            :
                            this.renderFullTable()
                        }
                    </Col>
                </Row>
            </StyledCard>
        );
    }
}

export default injectIntl(ChannelInstanceHistory);
